import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ThumbsUp, ThumbsDown, Database, FileText, ArrowUpRight, GraduationCap, Sparkles } from "lucide-react";
import { AnswerChart } from "./AnswerChart";
import { Evidence } from "./Evidence";
import { visibleText } from "./api";

const Actions = ({ msg, onRate, onDown, onDetails, selected, inlineDetails, setInline }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
    {msg.evidence?.length > 0 && (
      <button className={`btn btn-sm ${selected || inlineDetails ? "btn-primary" : ""}`} onClick={() => { onDetails(msg.id); setInline(!inlineDetails); }} data-testid={`ai2-how-${msg.id}`}>
        <Database size={12} />How I got this
      </button>
    )}
    <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
      <button className={`btn btn-sm ${msg.rating === "up" ? "btn-primary" : "btn-ghost"}`} onClick={() => onRate(msg)} aria-label="Good answer" disabled={!!msg.rating} data-testid={`ai2-up-${msg.id}`}><ThumbsUp size={13} /></button>
      <button className={`btn btn-sm ${msg.rating === "down" ? "btn-danger" : "btn-ghost"}`} onClick={() => onDown(msg)} aria-label="Wrong answer" disabled={!!msg.rating} data-testid={`ai2-down-${msg.id}`}><ThumbsDown size={13} /></button>
    </div>
  </div>
);

export const AssistantMessage = ({ msg, streaming, status, onFollow, onRate, onDown, onDetails, selected, onSource }) => {
  const [inline, setInline] = useState(false);
  const text = visibleText(msg.content);
  return (
    <div className="card ai2-bot enter" data-testid={`ai2-answer-${msg.id || "streaming"}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }} className="label"><Sparkles size={12} color="#1F8A7C" />AI Insights</div>
      {msg.rules_used?.length > 0 && (
        <div className="chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "3px 8px", fontFamily: "inherit", fontSize: 12 }} data-testid={`ai2-rule-note-${msg.id}`}>
          <GraduationCap size={12} />Using a correction from {msg.rules_used.map((r) => `${r.by}, ${new Date(r.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}`).join("; ")}
        </div>
      )}
      {!text && streaming && <div className="ai2-status" data-testid="ai2-status"><span className="ai2-dot" />{status || "Thinking…"}</div>}
      {text && <div className={`ai2-md ${streaming ? "ai2-caret" : ""}`} data-testid="ai2-answer-text"><ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>}
      {text && streaming && status && <div className="ai2-status" style={{ marginTop: 6 }}><span className="ai2-dot" />{status}</div>}
      {(msg.charts || []).map((c, i) => <AnswerChart key={i} chart={c} idx={i} />)}
      {msg.sources?.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12, alignItems: "center" }} data-testid={`ai2-sources-${msg.id}`}>
          <span className="muted" style={{ fontSize: 12 }}>Source:</span>
          {msg.sources.map((s, i) => <button key={s.id} className="ai2-pill" style={{ cursor: "pointer" }} onClick={() => onSource(s)} data-testid={`ai2-source-${msg.id}-${i}`}><FileText size={12} color="#1F8A7C" />{s.title}</button>)}
        </div>
      )}
      {msg.suggestions?.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid={`ai2-suggestions-${msg.id}`}>
          <div className="label" style={{ marginBottom: 6 }}>Suggested next steps</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {msg.suggestions.map((s, i) => (
              <a key={i} href={s.path} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, textDecoration: "none" }} data-testid={`ai2-suggestion-${msg.id}-${i}`}><ArrowUpRight size={13} />{s.label}</a>
            ))}
          </div>
        </div>
      )}
      {!streaming && msg.id && !msg.local && <Actions msg={msg} onRate={onRate} onDown={onDown} onDetails={onDetails} selected={selected} inlineDetails={inline} setInline={setInline} />}
      {inline && <div className="ai2-mobile-inline" style={{ marginTop: 12 }}><Evidence msg={msg} /></div>}
      {msg.followups?.length > 0 && !streaming && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }} data-testid={`ai2-followups-${msg.id}`}>
          {msg.followups.map((f, i) => <button key={i} className="ai2-follow" onClick={() => onFollow(f)} data-testid={`ai2-followup-${msg.id}-${i}`}>{f}</button>)}
        </div>
      )}
    </div>
  );
};

export const UserMessage = ({ msg }) => <div className="ai2-user enter" data-testid="ai2-user-msg">{msg.content}</div>;
